import { IProduct } from '../../shared/models/product.model';
import { ArticleModel, IArticle } from '../../prisma-xs/models/article.model';
import { IDeviceConfiguration, IDevicePolesIDs, IDevicesOnGrid, IStockLevel } from '../models/device-data.model';
import { IGridPositionIndex, IMovingDevice, IPlacedDevice } from '../models/grid.model';
import { IBomModel } from '../../prisma-xs/models/bom.model';
import { IProductAccessories } from '../models/product-accessories.model';
import { uuidv4 } from '../../shared/helpers/grid-devices.helper';
import { PrimitiveProductAttributes } from '../models/side-bar-swb.model';
import { IProductCatalog, IProductGroup } from '../models/catalogue.model';
import { TableHeaderNamesConstants } from '../../ui/constants/table-header-names.constants';
import { checkForContinueSorting, performSorting, sortByOriginalSequence } from '../../ui/helpers/sort.helper';
import { isArticleExistsInBOMModel } from '../../prisma-xs/helpers/bom-mapper.helper';
import { SortingOrder } from '../../shared/models/table-sort.model';
import { ShowBadgeNewPipe } from '../pipes/show-badge-new.pipe';
import { IDevice } from '../../shared/models/device.model';

export const getDevicesFieldsWidth = (device: IProduct): number => {
  const modules = Number(device.modules);
  return device?.productCharacteristicsRules?.isCombBusBar ? 2 * modules : modules;
};

export const generateDeviceConfiguration = (device: IProduct): IDeviceConfiguration => {
  return {
    productRangeName: device?.productRange?.name,
    productRangeId: device?.productRangeId,
    poleName: device?.poleId === 0 ? '' : device?.poleName,
    productCatalogueTitle: device?.productCatalogueTitle,
    referenceNumber: device?.reference,
    enumber: device?.enumber,
    ratedCurrent: device?.ampere,
    curve: device?.kurve,
    breakingCapacity: device?.type,
    rcdType: device?.type2,
    leakageCurrent: device?.leakageCurrent,
    label: device?.productRange?.label,
    applicationGroup: device?.productRange?.applicationGroup,
    description: device.userDescription,
    isFeeder: false,
    feedingDevicesList: '',
    poleContactComposition: device?.poleContactCompositionSpec?.poleName,
    availableFunction: device?.availableFunction,
    specification: device.specification,
    status: device?.status,
    pDescription: device?.pDescription,
    channel: getProductFunctionOptionByType(device, PrimitiveProductAttributes.channel),
    protocol: getProductFunctionOptionByType(device, PrimitiveProductAttributes.protocol),
    certification: getProductFunctionOptionByType(device, PrimitiveProductAttributes.mid_certification),
    type: getProductFunctionOptionByType(device, PrimitiveProductAttributes.type),
    power: getProductFunctionOptionByType(device, PrimitiveProductAttributes.power),
    connection: getProductFunctionOptionByType(device, PrimitiveProductAttributes.connection),
    breaking_capacity_IEC_60947_2: getProductFunctionOptionByType(device, PrimitiveProductAttributes.breaking_capacity_IEC_60947_2),
    frameRating: getProductFunctionOptionByType(device, PrimitiveProductAttributes.frameRating),
    tripUnit: getProductFunctionOptionByType(device, PrimitiveProductAttributes.tripUnit),
    feeding: getProductFunctionOptionByType(device, PrimitiveProductAttributes.feeding),
    zigbee: getProductFunctionOptionByType(device, PrimitiveProductAttributes.zigbee),
    controlVoltage: getProductFunctionOptionByType(device, PrimitiveProductAttributes.controlVoltage),
    dataCharacteristicsLink: device?.presentationRules?.deviceProductCharacteristicsLink,
    hasShieldedAndUnshieldedVariant: device?.presentationRules?.hasShieldedAndUnshieldedVariant,
    maximumCurrent: getProductFunctionOptionByType(device, PrimitiveProductAttributes.maximumCurrent),
    supplyVoltage: getProductFunctionOptionByType(device, PrimitiveProductAttributes.supplyVoltage),
    maximumDischargeCurrent: getProductFunctionOptionByType(device, PrimitiveProductAttributes.maximumDischargeCurrent),
    channelNumber: getProductFunctionOptionByType(device, PrimitiveProductAttributes.channelNumber),
    supplyCurrent: getProductFunctionOptionByType(device, PrimitiveProductAttributes.supplyCurrent),
    binaryInputs: getProductFunctionOptionByType(device, PrimitiveProductAttributes.binaryInputs),
    bidirectional: getProductFunctionOptionByType(device, PrimitiveProductAttributes.bidirectional)
  };
};

export const getProductFunctionOptionByType = (product: IProduct, type: PrimitiveProductAttributes) => {
  return product?.productFunctions?.find(f => f.function === type)?.value;
}

export const firstMissingNumberInArray = (sequenceOfNumbers: number[]): number => {
  const  count = sequenceOfNumbers.length;
  const missing = [];

  for (let i = 1; i <= count; i++) {
    if (sequenceOfNumbers.indexOf(i) === -1) {
      missing.push(i);
      return i;
    }
  }
  return undefined;
};

export const getLabelMatcher = (label: string): RegExpMatchArray => {
  return label.match(/\d+$/);
};

export const getLastNumberInLabelMatcher = (label: string): RegExpMatchArray => {
  return label.match(/(\d+)(?!.*\d)/);
}

export const hasNumber = (label: string): boolean => {
  return /\d/.test(label);
}

export const hasLetters = (label: string): boolean => {
  return /[a-zA-Z]/.test(label);
}

export const endsWithLetter = (label: string): boolean => {
  return /[a-zA-Z]$/.test(label);
}

export const generateDeviceLabel = (label: string, placedDevicesLabels: string[]): string => {
  if (hasNumber(label) && hasLetters(label) && endsWithLetter(label)) {
    return generateLabelIfDeviceContainsNumberAndEndsWithLetter(label, placedDevicesLabels);
  }
  const devicesWidthSameLabel = [];
  const labelNumberArray = [];
  let currentLabelWithoutNumber = label;
  const currentLabelMatcher = getLabelMatcher(label);
  if (currentLabelMatcher) {
    const [currentDeviceLabelNumber] = currentLabelMatcher;
    currentLabelWithoutNumber = getLabelWithoutNumber(label, currentDeviceLabelNumber);
  }
  placedDevicesLabels.forEach(label => {
    if (label) {
      const labelMatcher = getLabelMatcher(label);
      if (labelMatcher) {
        // Device has number in label
        const [deviceLabelNumber] = labelMatcher;
        const labelWithoutNumber = getLabelWithoutNumber(label, deviceLabelNumber);

        if (labelWithoutNumber === currentLabelWithoutNumber) {
          devicesWidthSameLabel.push(label);

          const labelNumber = parseInt(deviceLabelNumber, 10);
          if (labelNumber) {
            labelNumberArray.push(labelNumber);
          }
        }
      } else {
        // Device has no number in label
        if (label === label) {
          devicesWidthSameLabel.push(label);
        }
      }
    }
  });

  if (!labelNumberArray.length || !devicesWidthSameLabel.length) {
    return label + '1';
  }
  const missing = firstMissingNumberInArray(labelNumberArray);

  return missing ? currentLabelWithoutNumber + missing : currentLabelWithoutNumber + (labelNumberArray.length + 1);
};

export const getLabelWithoutNumber = (label:string, deviceNumber: string): string => {
  const indexOfNumberInString = label.lastIndexOf(deviceNumber);
  return label.slice(0, indexOfNumberInString);
};

export const generateLabelWithNumberBetweenLetters = (label: string,
                                                      numberInLabel: string,
                                                      labelMatcherIndex: number,
                                                      labelNumberArray: number[]): string => {
  const missing = firstMissingNumberInArray(labelNumberArray);

  return label.slice(0, labelMatcherIndex) +
    (missing || labelNumberArray.length + 1) +
    label.slice(labelMatcherIndex + numberInLabel.length, label.length);
};

export const getAllNumbersOfEqualLabelsWithoutNumber = (placedDevicesLabels: string[],
                                                        labelMatcherIndex: number,
                                                        labelWithoutLastNumber: string): number[] => {
  const labelNumberArray = [];

  placedDevicesLabels.forEach(label => {
    const deviceLabelMatcher = getLastNumberInLabelMatcher(label);
    if (!deviceLabelMatcher) {
      return
    }

    if (removeNumberFromLabel(label, deviceLabelMatcher[0], labelMatcherIndex) === labelWithoutLastNumber) {
      labelNumberArray.push(Number(deviceLabelMatcher[0]));
    }
  });

  return labelNumberArray;
};

export const removeNumberFromLabel = (label: string, numberInLabel: string, numberIndex: number): string => {
  const firstPart = label.slice(0, numberIndex);
  const secondPart = label.slice(numberIndex + numberInLabel.length, label.length);

  return firstPart + secondPart;
}

export const generateLabelIfDeviceContainsNumberAndEndsWithLetter = (label: string, placedDevicesLabels: string[]): string => {
  const labelMatcher = getLastNumberInLabelMatcher(label);
  if (!labelMatcher) {
    return null;
  }
  const [numberInLabel] = labelMatcher;
  const labelWithoutLastNumber = removeNumberFromLabel(label, numberInLabel, labelMatcher.index);
  const labelNumberArray = getAllNumbersOfEqualLabelsWithoutNumber(placedDevicesLabels, labelMatcher.index, labelWithoutLastNumber);

  return generateLabelWithNumberBetweenLetters(label, numberInLabel, labelMatcher.index, labelNumberArray);
}

const simplyAddItemToList = (bomArticles: IBomModel[], item: IProductAccessories, article: ArticleModel) => {
  bomArticles.push({
    quantity: item.quantity,
    article
  });
}

const addItemToListAndMergeQuantity = (bomArticles: IBomModel[], item: IProductAccessories, article: ArticleModel) => {
  const indexInBOMModel = isArticleExistsInBOMModel(bomArticles, article);

  indexInBOMModel >= 0 ?
    addQuantityInArticle(bomArticles[indexInBOMModel], item.quantity) :
    simplyAddItemToList(bomArticles, item, article);
}

export const addQuantityInArticle = (bomArticle: IBomModel, quantity: number) => {
  bomArticle.quantity += quantity;
}

export const mapAccessoriesItemsToBomModels = (items: IProductAccessories[], mergeQuantities: boolean = false): IBomModel[] => {
  const bomArticles: IBomModel[] = [];

  items.forEach((item: IProductAccessories) => {
    const article = new ArticleModel();
    article.id = item.additionalBOMEntryId;
    article.eNumber = Number(item.enumber);
    article.ref = item.reference;
    article.description = item.description;
    article.price = item.price;
    article.deliverToCustomer = item.deliverToCustomer;
    article.deliverToPanelBuilder = item.deliverToPanelBuilder;
    article.status = item.status;
    article.isCustomDevice = item.isCustomDevice;
    article.enclosureId = item.enclosureId ?? undefined;
    article.productCharacteristicsLink = item?.productCharacteristicsLink;
    article.productInfoLink= item?.productInfoLink;
    article.canTransferToWholesaler = item.canTransferToWholesaler;
    article.priceBeforeDiscount = item.priceBeforeDiscount;

    mergeQuantities ? addItemToListAndMergeQuantity(bomArticles, item, article) : simplyAddItemToList(bomArticles, item, article);
  });

  return bomArticles;
};


export const convertIDeviceToIPlacedDevices = (devices: IDevicesOnGrid[]): IPlacedDevice[] => {
  return devices
    .map(device => {
      return {
        id: device.id,
        uuid: uuidv4(),
        position: { top: device.rowNumber, left: device.columnNumber },
        enclosureField: { top: device?.enclosureRow, left: device?.enclosureField },
        options: device?.deviceOptions?.split(','),
        device: {
          ...device.product,
          poleName: device.product?.productPole?.poleName || '',
          productRange: {
            ...device.product.productRange,
            label: device.label,
            applicationGroup: { ...device.applicationGroup },
            description: device.userdescription,
          },
          userDescription: device.userdescription,
          productCatalogueTitle: device.product.productCatalogueTitle,
          applicationGroupId: device.applicationGroupId,
          sizeRules:
            device?.product?.productCharacteristicsRules?.isCombBusBar ?
              {
                minRequiredNumberOfModules: device.product.sizeRules.minRequiredNumberOfModules,
                minSize: device.product.sizeRules.minSize
              }
              : null
        },
        width: device.width,
        sldConnection: {
          ...device.sldConnection
        }
      };
    });
};

export const getSimilarProductWithRatedCurrent = (similarProducts: IProduct[], currentDevice: IProduct, ratedCurrent: number): IProduct => {
  return similarProducts.find(product =>
    parseInt(product.ampere, 10) === ratedCurrent
    && product.kurve === currentDevice.kurve
    && product.type === currentDevice.type && product.type2 === currentDevice.type2
    && product.leakageCurrent === currentDevice.leakageCurrent
    && product.poleContactCompositionSpec?.poleName === currentDevice.poleContactCompositionSpec?.poleName
    && product.availableFunction === currentDevice.availableFunction
  );
};

export const getSearchCountForFilteredGroup =
  (productsCatalog: IProductCatalog[], productGroups: IProductGroup[], enclosureNumberOfModulesPerRow: number[]) => {
    if(!productGroups || !productsCatalog || !enclosureNumberOfModulesPerRow) {
      return;
    }

    return productGroups.map(group => {
      return {
        ...group,
        searchCount: productsCatalog
          .filter(p => p.groupId === group.groupId)
          .reduce((count, catalog) => {
            return count + catalog?.products?.length;
          }, 0)
      }
    });
}

export const getNewDeviceSimilarProductWithRatedCurrent = (similarProducts: IProduct[],
                                                           device: IProduct,
                                                           ratedCurrent: number): IProduct => {
  const similarDevice = getSimilarProductWithRatedCurrent(similarProducts, device, ratedCurrent);
  if (!similarDevice) {
    return null;
  }

  return {
    ...similarDevice,
    productSupportSpec: {
      ...device.productSupportSpec
    },
    productRange: {
      ...device.productRange,
      label: null
    }
  };
}

export const sortProductsByKey = (bomModels: IBomModel[], key: TableHeaderNamesConstants, sortOrder: SortingOrder): IBomModel[] => {
  return bomModels?.concat().sort((a: IBomModel, b: IBomModel) => {

    if (checkForContinueSorting(a, b, key)) {
      return;
    }

    let a1;
    let b1;

    switch (key) {
      case TableHeaderNamesConstants.REFERENCE:
      case TableHeaderNamesConstants.ENUMBER:
      case TableHeaderNamesConstants.DESCRIPTION:
        a1 = a?.article[key];
        b1 = b?.article[key];

        return performSorting(a1, b1, sortOrder);
      case TableHeaderNamesConstants.DIRECT_DELIVERY:
      case TableHeaderNamesConstants.PREWIRING:
        a1 = a?.article[key];
        b1 = b?.article[key];

        return sortOrder ? Number(a1) - Number(b1) : Number(b1) - Number(a1);
      case TableHeaderNamesConstants.UNIT_PRICE:
      case TableHeaderNamesConstants.STOCK_LEVEL:
        a1 = a?.article[key];
        b1 = b?.article[key];

        return sortOrder ? b1 - a1 : a1 - b1;
      case TableHeaderNamesConstants.QUANTITY:
        a1 = a[key];
        b1 = b[key];

        return sortOrder ? b1 - a1 : a1 - b1;
      case TableHeaderNamesConstants.TOTAL_PRICE:
        a1 = a?.quantity * a?.article.price;
        b1 = b?.quantity * b?.article.price;

        return sortOrder ? b1 - a1 : a1 - b1;
    }
  });
};

export const executeSort = (models: IBomModel[],
                            key: TableHeaderNamesConstants,
                            sortOrder: SortingOrder): IBomModel[] => {
  if(!key || sortOrder === null) {
    return models;
  }
  return sortOrder !== SortingOrder.NONE ? sortProductsByKey(models, key, sortOrder) : sortByOriginalSequence<IBomModel>(models);
}

export const addOriginalSequencingToBOM = (models: IBomModel[]): IBomModel[] => {
  if(!models) {
    return;
  }
  for(let i = 0; i < models.length; i++) {
    models[i].originalSequence = i;
  }
  return models;
}

export const getFirstNonEmptyProductGroup = (catalogueArticleSelectedType: number, productGroups: IProductGroup[]) => {
  const group: IProductGroup = productGroups?.find(g => g.searchCount > 0);
  const selectedGroup: IProductGroup = productGroups?.find(g => g.groupId === catalogueArticleSelectedType);

  if (selectedGroup?.searchCount > 0) {
    return selectedGroup?.groupId;
  }

  return group?.groupId ?? catalogueArticleSelectedType;
}

export const groupHasNewProduct = (groupId: number, productCatalog: IProductCatalog[]): boolean => {
  const group = productCatalog.filter(g => g.groupId === groupId);

  return group?.some(g => new ShowBadgeNewPipe().transform(g.products));
}

export const updateDeviceFactory = (projectId: number, device: IPlacedDevice, position: IGridPositionIndex) => {
  return {
    ...device,
    productTypeId: device?.device?.productTypeId,
    projectId,
    productId: device.device.productId,
    rowNumber: device.position.top,
    columnNumber: device.position.left,
    label: device.device.productRange.label,
    width: device.width,
    enclosureField: position.left,
    enclosureRow: position.top
  };
}

export const updateMultipleDevicesFactory = (projectId: number, devices: IPlacedDevice[], position: IGridPositionIndex) => {
  return devices.map(device => updateDeviceFactory(projectId, device, position))
}

export const poleAndRangeIdsAreEqual = (rangeAndPoleIds: IDevicePolesIDs, device: IProduct) => {
  return (rangeAndPoleIds?.productRangeId === device?.productRangeId)
    && (rangeAndPoleIds?.productPoleId === device?.poleId);
}

export const extractTypesFromArticle = (articles: IArticle[], kitType): string[] => {
  return [...new Set(articles.filter(article => article.kitType === kitType).map(article => article.productType))];
};

export const getStockLevelOfProduct = (productReference: string, devicesStockLevels: IStockLevel[]): number => {
  return devicesStockLevels.find(level => level.productRef === productReference)?.stockLevel;
}

export const mapDevicesAndReplacingProductsReferences = (devices: IDevice[]): Partial<IDevice>[] => {
  return [].concat(devices,
    devices
      .filter(replacing => replacing.product?.replacingProduct)
      .map(replacing => {
        return {
          product: replacing.product.replacingProduct
        }
      })
  );
}

export const convertFromMovingToPlacedDevice = (device: IMovingDevice): IPlacedDevice => {
  return {
    device: device.device,
    options: device.options,
    width: device.width,
    uuid: device.uuid,
    enclosureField: device.enclosureField,
    id: device.id,
    sldConnection: device.sldConnection,
    position: device.position
  }
};

export const bomArraysNotIdentical = (array1: IBomModel[], array2: number[]): boolean => {
  const idSet1 = new Set(array1.map((item) => item?.article?.id));

  // Check if any of these conditions are true:
  // 1. Not all IDs in array2 exist in array1
  // 2. The number of IDs in array1 and array2 are different
  return !array2?.every(id => idSet1.has(id)) || array2?.length !== idSet1.size;
}

export const allBomElementsAreSelected = (allBomModels: IBomModel[],
                                          selectedItems: number[],
                                          allBomAccessoriesModels: IBomModel[],
                                          selectedItemsAccessories: number[]): boolean => {
  if (bomArraysNotIdentical(allBomModels, selectedItems)) {
    return false;
  }

  if (bomArraysNotIdentical(allBomAccessoriesModels, selectedItemsAccessories)) {
    return false;
  }

  return true;
}
